// Error with a user-facing message and suggested action
export interface MappedError {
  message: string;
  suggestion: string;
  showLoginLink?: boolean;
}

// Cognito error code → user-friendly copy
export const COGNITO_ERROR_MESSAGES: Record<string, MappedError> = {
  UsernameExistsException: {
    message: 'An account with this email already exists.',
    suggestion: 'Already have an account?',
    showLoginLink: true,
  },
  InvalidPasswordException: {
    message: 'Password does not meet requirements.',
    suggestion:
      'Please ensure your password has at least 8 characters including uppercase, lowercase, number, and special character.',
    showLoginLink: false,
  },
  InvalidParameterException: {
    message: 'Invalid input provided.',
    suggestion: 'Please check your email and password and try again.',
    showLoginLink: false,
  },
  CodeMismatchException: {
    message: 'The verification code is incorrect.',
    suggestion: 'Please check the code from your email and try again.',
    showLoginLink: false,
  },
  ExpiredCodeException: {
    message: 'The verification code has expired.',
    suggestion: 'Please request a new code and try again.',
    showLoginLink: false,
  },
  LimitExceededException: {
    message: 'Too many attempts.',
    suggestion: 'Please wait a few minutes before trying again.',
    showLoginLink: false,
  },
  NotAuthorizedException: {
    message: 'Incorrect email or password.',
    suggestion: 'Please check your credentials and try again.',
    showLoginLink: false,
  },
  UserNotFoundException: {
    message: 'No account found with this email.',
    suggestion: 'Please check your email or create a new account.',
    showLoginLink: false,
  },
  UserNotConfirmedException: {
    message: 'Your email has not been verified.',
    suggestion: 'Please check your email for the verification code.',
    showLoginLink: false,
  },
  TooManyRequestsException: {
    message: 'Too many requests.',
    suggestion: 'Please wait a few minutes before trying again.',
    showLoginLink: false,
  },
  TooManyFailedAttemptsException: {
    message: 'Too many failed attempts.',
    suggestion:
      'Your account has been temporarily locked. Please try again later.',
    showLoginLink: false,
  },
  // Amplify v6 refuses to sign in over a live session. AuthContext resolves
  // this automatically (same user => success, other user => switch), so
  // reaching the UI means the recovery itself failed (#261).
  UserAlreadyAuthenticatedException: {
    message: "You're already signed in.",
    suggestion: 'Refresh the page to continue, or sign out first to use a different account.',
    showLoginLink: false,
  },
};

// Maps Cognito/Amplify v6 errors to user-friendly messages, falling back to message-pattern matching
export function mapCognitoError(error: any): MappedError {
  if (!error) {
    return {
      message: 'An unexpected error occurred.',
      suggestion: 'Please try again.',
      showLoginLink: false,
    };
  }

  // Amplify surfaces the code under different fields depending on version
  const errorName =
    error.name || error.code || error.__type || error.constructor?.name;

  if (
    error.message?.includes('Network') ||
    error.message?.includes('Failed to fetch') ||
    errorName === 'NetworkError'
  ) {
    return {
      message: 'Network connection error.',
      suggestion: 'Please check your internet connection and try again.',
      showLoginLink: false,
    };
  }

  if (errorName && COGNITO_ERROR_MESSAGES[errorName]) {
    return COGNITO_ERROR_MESSAGES[errorName];
  }

  const errorMessage = error.message || '';

  if (errorMessage.includes('already exists')) {
    return COGNITO_ERROR_MESSAGES.UsernameExistsException;
  }

  if (
    errorMessage.includes('password') &&
    (errorMessage.includes('requirements') ||
      errorMessage.includes('policy') ||
      errorMessage.includes('conform'))
  ) {
    return COGNITO_ERROR_MESSAGES.InvalidPasswordException;
  }

  if (errorMessage.includes('code') && errorMessage.includes('mismatch')) {
    return COGNITO_ERROR_MESSAGES.CodeMismatchException;
  }

  if (errorMessage.includes('code') && errorMessage.includes('expired')) {
    return COGNITO_ERROR_MESSAGES.ExpiredCodeException;
  }

  if (errorMessage.includes('too many')) {
    return COGNITO_ERROR_MESSAGES.TooManyRequestsException;
  }

  return {
    message: errorMessage || 'An error occurred during authentication.',
    suggestion: 'Please try again or contact support if the problem persists.',
    showLoginLink: false,
  };
}

// An invited teammate who hasn't signed in yet (Cognito FORCE_CHANGE_PASSWORD)
// has no password of their own to reset: the invite's temporary one is the
// way in, and they choose theirs at first sign-in (#395).
export const INVITED_TEAMMATE_RESET_ERROR: MappedError = {
  message: "This account doesn't have a password to reset yet.",
  suggestion:
    'If you were invited to a team, sign in with the temporary password from your invite email. You will choose your own password then.',
  showLoginLink: false,
};

// Errors from requesting a reset code (ForgotPassword). For a teammate still on
// the invite's temporary password, Cognito answers NotAuthorizedException ("User
// password cannot be reset in the current state."), which the generic mapping
// would call a wrong password. Our app client prevents user-existence errors,
// so Cognito may instead pretend to send a code: the reset page says what to
// do when none arrives.
export function mapResetRequestError(error: any): MappedError {
  const errorName = error?.name || error?.code || error?.__type;
  if (errorName === 'NotAuthorizedException' || /cannot be reset in the current state/i.test(error?.message ?? '')) {
    return INVITED_TEAMMATE_RESET_ERROR;
  }
  return mapCognitoError(error);
}

// One-line "message + suggestion" for display
export function formatErrorMessage(mappedError: MappedError): string {
  return `${mappedError.message} ${mappedError.suggestion}`;
}
